import { CSSProperties, FC, ReactNode } from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';

import { BACKGROUND, PEARL, PRIMARY, STROKE, TEXT } from '../../Helpers/colors';

export const HoverPrimary = {
	background: PRIMARY.BASE,
	boxShadow: '0px 4px 8px -2px rgba(0, 0, 0, 0.12), 0px 2px 4px -2px rgba(0, 0, 0, 0.04)',
};

export const HoverSecondary = {
	background: PRIMARY.SECONDARY,
	boxShadow: 'none',
};

export const HoverSecondaryGray = {
	background: PEARL.TERTIARY,
	boxShadow: 'none',
};

export const HoverTertiary = {
	background: BACKGROUND.PRIMARY[100],
	boxShadow: '0px 4px 8px -2px rgba(0, 0, 0, 0.12), 0px 2px 4px -2px rgba(0, 0, 0, 0.04)',
};

export const HoverQuanternary = {
	background: PEARL.QUATERNARY,
};

export const HoverOutlined = {
  background: PRIMARY.QUATERNARY,
  boxShadow: "0px 4px 8px -2px rgba(0, 0, 0, 0.12), 0px 2px 4px -2px rgba(0, 0, 0, 0.04)",
};

export const HoverLink = {
	background: BACKGROUND.PRIMARY[100],
	color: PRIMARY.DARK[16],
	boxShadow: 'none',
	textDecoration: 'underline',
};

export const FocusPrimary = {
	background: PRIMARY.DARK[16],
	boxShadow: PRIMARY.TERTIARY,
	border: 'none',
};

export const FocusSecondary = {
	background: PRIMARY.PRIMARY,
	boxShadow: 'none',
};

export const FocusSecondaryGray = {
	background: PEARL.SECONDARY,
	boxShadow: 'none',
};

export const FocusTertiary = {
	background: BACKGROUND.PRIMARY[100],
	boxShadow: '0px 0px 0px 4px rgba(122, 121, 119, 0.12)',
};

export const FocusQuanternary = {
	background: PEARL.QUATERNARY,
};

export const FocusOutlined = {
	background: PRIMARY.TERTIARY,
};

const HoverSettings = {
	primary: HoverPrimary,
	secondary: HoverSecondary,
	secondaryGray: HoverSecondaryGray,
	tertiary: HoverTertiary,
	quaternary: HoverQuanternary,
	outlined: HoverOutlined,
	link: HoverLink,
};

const FocusSettings = {
	primary: FocusPrimary,
	secondary: FocusSecondary,
	secondaryGray: FocusSecondaryGray,
	tertiary: FocusTertiary,
	quaternary: FocusQuanternary,
	outlined: FocusOutlined,
};

type ButtonType = 'primary' | 'secondary' | 'secondaryGray' | 'tertiary' | 'quaternary' | 'outlined' | 'link';

type SizeType = 'sm' | 'md' | 'lg' | 'xlg' | 'xxlg' | 'xxxlg';

const paddings = {
	sm: '8px 12px',
	md: '10px 16px',
	lg: '12px 20px',
	xlg: '12px 20px',
	xxlg: '14px 24px',
	xxxlg: '14px 28px',
};

const radiuses = {
	sm: '8px',
	md: '9px',
	lg: '10px',
	xlg: '12px',
	xxlg: '13px',
	xxxlg: '14px',
};

const heights = {
	sm: '36px',
	md: '40px',
	lg: '44px',
	xlg: '48px',
	xxlg: '52px',
	xxxlg: '56px',
};

const backgroundColor = {
	primary: PRIMARY.BASE,
	secondary: PRIMARY.QUATERNARY,
	secondaryGray: PEARL.QUATERNARY,
	tertiary: BACKGROUND.PRIMARY[100],
	quaternary: 'transparent',
	outlined: BACKGROUND.PRIMARY[100],
	link: BACKGROUND.PRIMARY[100],
};

const textColor = {
	primary: BACKGROUND.PRIMARY[100],
	secondary: PRIMARY.BASE,
	secondaryGray: TEXT.TERTIARY,
	tertiary: TEXT.SECONDARY,
	quaternary: TEXT.TERTIARY,
	outlined: PRIMARY.BASE,
	link: PRIMARY.BASE,
};

const border = {
	primary: `1px solid ${STROKE.TERTIARY}`,
	secondary: 'none',
	secondaryGray: 'none',
	tertiary: `1px solid ${STROKE.SECONDARY}`,
	quaternary: 'none',
	outlined: `1.6px solid ${PRIMARY.BASE}`,
	link: 'none',
};

const boxShadow = {
	primary: `0px 1px 2px 0px ${STROKE.TERTIARY}`,
	secondary: 'none',
	secondaryGray: 'none',
	tertiary: `0px 1px 2px 0px ${STROKE.TERTIARY}`,
	quaternary: 'none',
	outlined: 'none',
	link: 'none',
};

interface CustomButtonProperties extends ButtonProps {
	buttonType?: ButtonType;
	iconOnly?: ReactNode;
	loading?: boolean;
	sizeType?: SizeType;
	style?: CSSProperties;
}

const CustomizedButton = styled(Button, {
	shouldForwardProp: (prop) => prop !== 'buttonType' && prop !== 'sizeType',
})<CustomButtonProperties>(({ sizeType = 'sm', buttonType = 'primary' }) => ({
	display: 'inline-flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '8px',
	fontFamily: 'Inter',
	textTransform: 'none',
	width: '100%',
	height: heights[sizeType],
	minWidth: '44px',
	lineHeight: '20px',
	fontWeight: 500,
	fontStyle: 'normal',
	fontSize: '14px',
	textAlign: 'center',
	whiteSpace: 'nowrap',
	border: border[buttonType],
	padding: paddings[sizeType],
	borderRadius: radiuses[sizeType],
	boxShadow: boxShadow[buttonType],
	color: textColor[buttonType],
	background: backgroundColor[buttonType],
	'&:hover': HoverSettings[buttonType],
	'&:focus': FocusSettings[buttonType],
	'&:disabled': {
		border: 'none',
		backgroundColor: PEARL.TERTIARY,
		color: BACKGROUND.PRIMARY[100],
	},
}));

const CustomButton: FC<CustomButtonProperties> = ({
	title,
	onClick,
	type = 'button',
	loading = false,
	disabled = false,
	style,
	sizeType,
	buttonType,
	startIcon,
	endIcon,
	iconOnly,
	className,
	...rest
}) => (
	<div className={className}>
		<CustomizedButton
			type={type}
			variant="contained"
			disabled={disabled || loading}
			onClick={onClick}
			style={style}
			sizeType={sizeType}
			buttonType={buttonType}
			{...rest}
		>
			{loading && <CircularProgress size={24} color="inherit" />}
			{!loading && startIcon && !iconOnly && startIcon}
			{!loading && !iconOnly ? title : iconOnly}
			{!loading && endIcon && !iconOnly && endIcon}
		</CustomizedButton>
	</div>
);

export default CustomButton;
